import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabase/supabase";
import { useAuth } from "../../contexts/AuthContext";

// Lightweight styles scoped to this page only
const styles = {
  page: { padding: 16, maxWidth: 720, margin: "0 auto" },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 4px",
    position: "sticky",
    top: 0,
    background: "#fff",
    zIndex: 1,
  },
  backBtn: {
    width: 42,
    height: 42,
    borderRadius: 12,
    border: "1px solid rgba(0,0,0,0.06)",
    background: "linear-gradient(180deg, rgba(255,255,255,0.95), rgba(248,249,251,0.9))",
    color: "#111827",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
    transition: "transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease",
    backdropFilter: "saturate(1.2) blur(6px)",
  },
  title: { fontSize: 22, fontWeight: 600 },
  save: { background: "#000", color: "#fff", borderRadius: 20, padding: "8px 16px", border: 0, cursor: "pointer" },
  helper: { color: "#6b7280", margin: "8px 0 12px" },
  box: { background: "#f3f4f6", borderRadius: 12, padding: 12 },
  row: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 10px", background: "#fff", borderRadius: 10, margin: "8px 0" },
  drag: { cursor: "grab", marginRight: 10 },
  remove: { color: "#ef4444", fontSize: 18, cursor: "pointer" },
  chips: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 16 },
  chip: (active) => ({ padding: "10px 14px", borderRadius: 22, background: active ? "#000" : "#e5e7eb", color: active ? "#fff" : "#6b7280", cursor: active ? "default" : "pointer" }),
};

const ALL_STATS = [
  { key: "posts", label: "posts" },
  { key: "likes", label: "likes" },
  { key: "donates", label: "donates" },
  { key: "followers", label: "followers" },
  { key: "following", label: "following" },
  { key: "saves", label: "saves" },
  { key: "locked", label: "locked content" },
];

export default function CustomizeStats() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(["posts", "likes", "donates"]);
  const [dragIndex, setDragIndex] = useState(null);

  const selectedDetails = useMemo(() => selected.map(k => ALL_STATS.find(s => s.key === k)).filter(Boolean), [selected]);

  const load = useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("stats")
        .eq("user_id", currentUser.id)
        .single();
      if (error) throw error;
      const arr = Array.isArray(data?.stats?.customStats)
        ? data.stats.customStats
        : Array.isArray(data?.stats?.selected)
        ? data.stats.selected
        : ["posts", "likes", "donates"];
      setSelected(arr.slice(0, 3));
    } catch (e) {
      // fallback already set
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => { load(); }, [load]);

  const onAdd = (key) => {
    if (selected.includes(key) || selected.length >= 3) return;
    setSelected(prev => [...prev, key]);
  };

  const onRemove = (key) => {
    setSelected(prev => prev.filter(k => k !== key));
  };

  // Basic HTML5 drag reorder
  const onDragStart = (index) => setDragIndex(index);
  const onDragOver = (e, overIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === overIndex) return;
    setSelected(prev => {
      const next = prev.slice();
      const [moved] = next.splice(dragIndex, 1);
      next.splice(overIndex, 0, moved);
      return next;
    });
    setDragIndex(overIndex);
  };

  const onSave = async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      // fetch existing stats to preserve other flags such as show_stats
      const { data: existing } = await supabase
        .from("profiles")
        .select("stats")
        .eq("user_id", currentUser.id)
        .single();
      const show = existing?.stats?.showStats ?? existing?.stats?.show_stats ?? false;
      // Write customStats for mobile app, and keep selected for web compatibility
      const nextStats = { 
        ...(existing?.stats || {}), 
        customStats: selected,
        selected: selected,
        showStats: !!show, 
        show_stats: !!show 
      };
      const { error } = await supabase
        .from("profiles")
        .update({ stats: nextStats })
        .eq("user_id", currentUser.id);
      if (error) throw error;
      navigate(-1);
    } catch (e) {
      alert(e?.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <button className="cs-back-btn" style={styles.backBtn} onClick={() => navigate(-1)} aria-label="Back">←</button>
        <div style={styles.title}>Customize Stats</div>
        <button style={styles.save} onClick={onSave} disabled={loading}>Save</button>
      </div>

      <p style={styles.helper}>Choose up to 3 stats to display on your profile</p>
      <p style={{ ...styles.helper, fontSize: 14 }}>Drag to reorder</p>

      <div style={styles.box}>
        {selectedDetails.map((item, index) => (
          <div
            key={item.key}
            style={styles.row}
            draggable
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(e, index)}
          >
            <span style={styles.drag}>≡</span>
            <span style={{ flex: 1 }}>{item.label}</span>
            <span style={styles.remove} onClick={() => onRemove(item.key)}>⊖</span>
          </div>
        ))}
      </div>

      <div style={styles.chips}>
        {ALL_STATS.map(s => {
          const isActive = selected.includes(s.key);
          return (
            <span
              key={s.key}
              style={styles.chip(isActive)}
              onClick={() => !isActive && onAdd(s.key)}
            >
              {isActive ? "✓ " : ""}{s.label}
            </span>
          );
        })}
      </div>
    </div>
  );
}


